"use client";

import { colors } from "@mui/material";
import Box, { BoxProps } from "@mui/material/Box";
import SwiperNextButton from "@v2/components/swiper/SwiperNextButton";
import SwiperPrevButton from "@v2/components/swiper/SwiperPrevButton";
import SwiperProgressBar from "@v2/components/swiper/SwiperProgressBar";
import Image from "next/image";
import Link from "next/link";
import { FC, useRef } from "react";
import { Autoplay, Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

export type SliderItem = {
    id: number;
    image: {
        id: string | null;
        sm_id: string | null;
        alt: string | null;
    };
    title: string | null;
    button_link: string | null;
    description: string | null;
    button_title: string | null;
    show_in_site: boolean;
};

const Slider = ({ content }: {
    content: SliderItem[];
}) => {
    const autoplayProgressRef = useRef(null);
    const onAutoplayTimeLeft = (s, time, progress) => {
        autoplayProgressRef.current?.setPercent((1 - progress) * 100);
    };

    return (
        <Box sx={{
            '& .swiper-pagination': {
                bottom: { xs: 24, md: 16 },
                '& .swiper-pagination-bullet': {
                    borderRadius: 3,
                    transition: '250ms !important',
                    background: colors.grey[200],
                },
                '& .swiper-pagination-bullet-active': {
                    background: colors.grey[50],
                    width: 24,
                }
            }
        }}>
            <Swiper
                slidesPerView={1}
                modules={[Pagination, Autoplay]}
                speed={450}
                autoplay={{
                    delay: 8000,
                    pauseOnMouseEnter: true
                }}
                onAutoplayTimeLeft={onAutoplayTimeLeft}
                pagination={{
                    enabled: true,
                    dynamicBullets: true,
                    dynamicMainBullets: 3,
                }}
                loop>
                <SwiperNextButton />
                <SwiperPrevButton />
                <SwiperProgressBar ref={autoplayProgressRef} />
                {content.filter(i => i.show_in_site)?.map((slide, index) => (
                    <SwiperSlide key={slide.id}>
                        <SliderImage
                            desktopId={slide.image.id}
                            mobileId={slide.image.sm_id}
                            priority={index === 0}
                            link={slide?.button_link}
                        />
                    </SwiperSlide>
                ))}
            </Swiper>
        </Box>
    );
};

const MEDIA_BASE = process.env.NEXT_PUBLIC_MEDIA_URL + "/api/v1/media/";

const SliderImage: FC<{
    desktopId: string | null,
    mobileId: string | null,
    link?: string,
    priority?: boolean
} & BoxProps> = ({ desktopId, mobileId, priority, link }) => {
    const linkProps = link ? { component: Link, href: link, target: '_blank' } : {};

    return (
        <>
            {desktopId && (
                <Box
                    sx={{ width: '100%', aspectRatio: '3 / 1', position: 'relative', display: { xs: 'none', md: 'block' } }}
                    {...linkProps}
                >
                    <Image
                        unoptimized
                        src={`${MEDIA_BASE}${desktopId}?height=890&quality=85`}
                        alt="slider"
                        priority={priority}
                        loading={priority ? 'eager' : 'lazy'}
                        fetchPriority={priority ? 'high' : 'low'}
                        fill
                        style={{ objectFit: 'cover' }}
                    />
                </Box>
            )}
            {mobileId && (
                <Box
                    sx={{ width: '100%', aspectRatio: '1 / 1', position: 'relative', display: { xs: 'block', md: 'none' } }}
                    {...linkProps}
                >
                    <Image
                        unoptimized
                        src={`${MEDIA_BASE}${mobileId}?height=600&quality=85`}
                        alt="slider"
                        priority={priority}
                        loading={priority ? 'eager' : 'lazy'}
                        fetchPriority={priority ? 'high' : 'low'}
                        fill
                        style={{ objectFit: 'cover' }}
                    />
                </Box>
            )}
        </>
    );
};

export default Slider;
